/**
 * USE_AUDIO_STORE.JS - Store Zustand pour l'état audio global
 * 
 * Ce store gère l'état global du système audio de l'application.
 * Il est utilisé par le AudioProvider et accessible dans tous les composants.
 * 
 * ÉTAT GÉRÉ:
 * - masterVolume: Volume principal (0-1)
 * - muted: État du mute (true/false)
 * - sounds: Objets Howl chargés et prêts
 * 
 * ACTIONS:
 * - setMasterVolume: Modifier le volume principal
 * - toggleMute: Basculer mute on/off
 * - setMuted: Définir l'état mute directement
 * - setSounds: Définir les sons chargés
 * 
 * AVANTAGES DE ZUSTAND:
 * - Plus performant que Context API pour état fréquemment mis à jour
 * - Pas de re-render inutile des composants
 * - API simple et intuitive
 * - Pas besoin de Provider wrapper (optionnel)
 */

import { create } from 'zustand'

export const useAudioStore = create((set, get) => ({
  // ============================================
  // ÉTAT
  // ============================================
  
  /**
   * Volume principal de l'application (0.0 à 1.0)
   * Appliqué globalement via Howler.volume()
   */
  masterVolume: 0.5,
  
  /**
   * État du mute
   * - true: Tous les sons sont coupés
   * - false: Sons audibles selon masterVolume
   */
  muted: false,
  
  /**
   * Dictionnaire des sons chargés
   * Structure: {
   *   [soundName]: {
   *     instances: [Howl, Howl, ...],  // Instances Howl (une par fichier)
   *     lastTime: number,                // Timestamp dernière lecture
   *     config: object                   // Config depuis SOUNDS_CONFIG
   *   }
   * }
   */
  sounds: {},
  
  // ============================================
  // ACTIONS
  // ============================================
  
  /**
   * Modifier le volume principal
   * 
   * @param {number} volume - Volume entre 0 et 1
   */
  setMasterVolume: (volume) => {
    // Validation: clamp entre 0 et 1
    const clampedVolume = Math.max(0, Math.min(1, volume))
    set({ masterVolume: clampedVolume })
    
    // Application via Howler (fait dans AudioProvider via listener)
  },
  
  /**
   * Basculer le mute (toggle)
   * Inverse l'état actuel
   */
  toggleMute: () => {
    set((state) => ({ muted: !state.muted }))
    
    // Application via Howler (fait dans AudioProvider via listener)
  },
  
  /**
   * Définir l'état mute directement
   * 
   * @param {boolean} muted - Nouvel état du mute
   */
  setMuted: (muted) => {
    set({ muted })
    
    // Application via Howler (fait dans AudioProvider via listener)
  },
  
  /**
   * Définir les sons chargés
   * Appelé par AudioProvider après chargement
   * 
   * @param {object} sounds - Dictionnaire des sons chargés
   */
  setSounds: (sounds) => {
    set({ sounds })
  },
  
  // ============================================
  // GETTERS (méthodes utilitaires)
  // ============================================
  
  /**
   * Obtenir un son spécifique par son nom
   * 
   * @param {string} name - Nom du son
   * @returns {object|null} Objet son ou null si inexistant
   */
  getSound: (name) => {
    const { sounds } = get()
    return sounds[name] || null
  },
  
  /**
   * Vérifier si les sons sont chargés
   * 
   * @returns {boolean} true si au moins un son est chargé
   */
  areSoundsLoaded: () => {
    const { sounds } = get()
    return Object.keys(sounds).length > 0
  }
}))

/**
 * UTILISATION DANS LES COMPOSANTS:
 * 
 * // Lecture simple
 * const { masterVolume, muted } = useAudioStore()
 * 
 * // Actions
 * const setMasterVolume = useAudioStore((state) => state.setMasterVolume)
 * const toggleMute = useAudioStore((state) => state.toggleMute)
 * 
 * // Sélection optimisée (évite re-render inutile)
 * const masterVolume = useAudioStore((state) => state.masterVolume)
 * 
 * // Accès sans abonnement (pas de re-render)
 * const volume = useAudioStore.getState().masterVolume
 * useAudioStore.getState().setMasterVolume(0.8)
 */

